import avatar_placeholder from "@assets/placeholder/avatar_placeholder.png";
import music_placeholder_1 from "@assets/placeholder/music_placeholder_1.webp";
import music_placeholder_2 from "@assets/placeholder/music_placeholder_2.webp";
import music_placeholder_3 from "@assets/placeholder/music_placeholder_3.webp";
import music_placeholder_4 from "@assets/placeholder/music_placeholder_4.webp";
import music_placeholder_5 from "@assets/placeholder/music_placeholder_5.webp";
import music_placeholder_6 from "@assets/placeholder/music_placeholder_6.webp";
import music_placeholder_7 from "@assets/placeholder/music_placeholder_7.webp";
import { getImageUrlWithSize } from "@utils/index";
import { Image, ImageProps } from "expo-image";
import _ from "lodash";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { StyleSheet, Platform, ImageSourcePropType } from "react-native";
import LBox, { LBoxProps } from "./LBox";
import useInternetStatus from "@utils/internetStatus";
import { useDownloads } from "@utils/downloadManager/hooks/useDownloads";
import { reconstructImagePath } from "@utils/downloadManager/utils";
import { captureException } from "@utils/captureException";
import {
  type ImageGroupKey,
  serializeKey,
  pathnameToKey,
  registerGroup,
  unregisterGroup,
  useImageGroupKeyOverride,
  useGroupEpoch,
} from "@utils/imageRequestGroups";
import { useNavigationStore } from "@utils/navigation/useNavigationStore";
import LText from "./LText";
import {
  getNextArtworkFallbackUrl,
  shouldRetryRemoteArtworkWithoutDiskCache,
  useArtworkRecovery,
} from "./LArtwork.recovery";

export const musicPlaceholders: ImageSourcePropType[] = [
  music_placeholder_1,
  music_placeholder_2,
  music_placeholder_3,
  music_placeholder_4,
  music_placeholder_5,
  music_placeholder_6,
  music_placeholder_7,
];

export const getDeterministicPlaceholder = (url?: string) => {
  if (!url || url.trim() === "") {
    return musicPlaceholders[0]; // Always use index 0 for undefined/empty URLs
  }

  // Simple hash function - sum of character codes
  const hash = url.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);

  const index = Math.abs(hash) % musicPlaceholders.length;
  return musicPlaceholders[index];
};

export type Transition = ImageProps["transition"] | "disabled";

export interface LArtworkProps extends LBoxProps {
  url?: string | null | (string | undefined | null)[];
  size?: number;
  imageSize?: "small" | "medium" | "large" | "xLarge";
  priority?: ImageProps["priority"];
  imageStyle?: ImageProps["style"];
  userAvatar?: boolean;
  blurhash?: string | null | (string | undefined | null)[];
  transition?: Transition;
  playlistHorizontal?: boolean;
  placeholderImage?: ImageSourcePropType;
  imageGroupKey?: ImageGroupKey;
  showTracklistChip?: boolean;
  tracklistChipCompact?: boolean;
}

// NOTE: Transitions disabled due to expo-image bug where images randomly
// get stuck at opacity: 0 on all platforms.
const defaultTransition = "disabled";
const REMOTE_ARTWORK_CACHE_POLICY: NonNullable<ImageProps["cachePolicy"]> =
  "disk";
const REMOTE_ARTWORK_CACHE_BYPASS_POLICY: NonNullable<
  ImageProps["cachePolicy"]
> = "none";

interface PureArtworkProps {
  url?: string;
  style: ImageProps["style"];
  priority: ImageProps["priority"];
  size?: number;
  userAvatar?: boolean;
  blurhash?: string | null;
  transition?: Transition;
  placeholderImage?: ImageSourcePropType;
  serializedGroupKey?: string;
}

const PureArtwork = ({
  priority,
  size,
  style: imageStyle,
  transition = defaultTransition,
  url,
  userAvatar = false,
  placeholderImage,
  serializedGroupKey,
}: PureArtworkProps) => {
  const downloadManager = useDownloads();
  const style = StyleSheet.flatten([
    {
      height: size ?? 50,
      aspectRatio: 1,
      // On web, allow pointer events to pass through for drag handling
      ...(Platform.OS === "web" ? { pointerEvents: "none" } : {}),
    },
    imageStyle,
  ]);

  const systemPlaceholder =
    placeholderImage ??
    (userAvatar ? avatar_placeholder : getDeterministicPlaceholder(url));

  const [placeholder, setPlaceholder] = useState<string | null>(null);
  // Reconstruct stale file:// paths (e.g. after iOS app update changes container UUID)
  const [stateUrl, setStateUrl] = useState(() => reconstructImagePath(url));
  const [hadError, setHadError] = useState(false);
  const [wasCancelled, setWasCancelled] = useState(false);
  const [diskCacheBypassNonce, setDiskCacheBypassNonce] = useState(0);

  const isConnected = useInternetStatus();
  const epoch = useGroupEpoch(serializedGroupKey);

  const loadStarted = useRef<number | null>(null);
  const timeoutRetryCount = useRef(0);
  const captureRecovery = useArtworkRecovery(url);

  const fixDeletedLocalUrl = (url: string) => {
    const isCurrentRecovery = captureRecovery();
    console.log(
      `LArtwork: tried to load file from cache that doesn't exist.`,
      url,
    );
    downloadManager
      .getOfflineLibrary()
      .then((cache) => {
        if (!isCurrentRecovery()) return;
        console.log(
          `LArtwork: cache`,
          cache?.map((x) => {
            if (x.type === "album") return x.image;
            if (x.type === "playlist") return x.images?.at(0);
            return null;
          }),
        );

        const imageId = url.split("/").at(-1);
        if (imageId) {
          const cachedDownloadItem = cache?.find((x) => {
            const imageUrl =
              x.type === "album"
                ? x.image
                : x.type === "playlist"
                  ? x.images?.at(0)
                  : null;
            return imageUrl?.includes(imageId);
          });
          console.log(`LArtwork: cached download item`, cachedDownloadItem);
          if (cachedDownloadItem) {
            const remoteUrl =
              cachedDownloadItem.type === "album"
                ? cachedDownloadItem.image
                : cachedDownloadItem.type === "playlist"
                  ? cachedDownloadItem.images?.at(0)
                  : undefined;
            console.log(
              `LArtwork: using cached download item remoteUrl:`,
              remoteUrl,
            );
            console.log(`LArtwork: signed remoteUrl`, remoteUrl);
            if (remoteUrl) {
              setStateUrl(remoteUrl);
            }
          }
        }
      })
      .catch(console.error);
  };

  const stateUrlRef = useRef(stateUrl);
  useEffect(() => {
    stateUrlRef.current = stateUrl;
  }, [stateUrl]);

  useEffect(() => {
    // if the url changes, update the stateUrl and the placeholder.
    // stateUrl is read via ref to avoid a loop when onError modifies it
    // (e.g. .jpg fallback) — re-running this effect would reset it back.
    const reconstructed = reconstructImagePath(url);
    if (reconstructed && reconstructed !== stateUrlRef.current) {
      setStateUrl(reconstructed);
      setPlaceholder(null);
      setDiskCacheBypassNonce(0);
      timeoutRetryCount.current = 0;
    }
  }, [url]);

  // Use the original url prop for recyclingKey so that error fallbacks
  // (which modify stateUrl) don't cause expo-image to blank the view
  // between retry attempts. recyclingKey only changes when a genuinely
  // new image is assigned from the parent.
  // Only include epoch in recyclingKey for images that were actually
  // cancelled — avoids reloading already-loaded images when switching tabs.
  const recyclingKey = useMemo(() => {
    const epochSuffix = wasCancelled ? `&_epoch=${epoch}` : "";
    const cacheBypassSuffix =
      diskCacheBypassNonce > 0 ? `&_cache_bypass=${diskCacheBypassNonce}` : "";
    if (hadError) {
      return `${url}&_nc=${
        isConnected ? "online" : "offline"
      }${epochSuffix}${cacheBypassSuffix}`;
    }
    return `${url}${epochSuffix}${cacheBypassSuffix}`;
  }, [url, isConnected, hadError, wasCancelled, epoch, diskCacheBypassNonce]);

  return (
    <Image
      recyclingKey={recyclingKey}
      placeholder={placeholder}
      placeholderContentFit="cover"
      cachePolicy={
        diskCacheBypassNonce > 0
          ? REMOTE_ARTWORK_CACHE_BYPASS_POLICY
          : REMOTE_ARTWORK_CACHE_POLICY
      }
      onPointerDown={(e) => {
        e.preventDefault();
      }}
      source={
        !stateUrl || placeholder
          ? systemPlaceholder
          : {
              uri: stateUrl === "reload_image" ? "" : stateUrl,
            }
      }
      contentFit="cover"
      priority={priority}
      {...(Platform.OS === "web" || !serializedGroupKey
        ? {}
        : { requestGroup: serializedGroupKey })}
      // Do not set `allowDownscaling` to false or you will face Erik's wrath due to wrecking the app's performance.
      // allowDownscaling={false}
      style={style}
      transition={transition === "disabled" ? undefined : transition}
      onError={(e) => {
        if (e.error === "cancelled") {
          setWasCancelled(true);
          return;
        }
        console.warn(`error loading image:`, stateUrl, e.error);
        captureException(
          new Error(`error loading image: ${url}, message: ${e.error}`),
          {
            channel: "breadcrumb",
            area: "image",
            operation: "load-artwork",
          },
        );
        setHadError(true);

        if (stateUrl === "reload_image") {
          return;
        }

        if (
          stateUrl &&
          !stateUrl.includes("file") &&
          diskCacheBypassNonce === 0 &&
          shouldRetryRemoteArtworkWithoutDiskCache(e.error)
        ) {
          setDiskCacheBypassNonce(Date.now());
          setPlaceholder(null);
          return;
        }

        if (
          stateUrl?.includes(".jpg") &&
          (e.error.includes("404") ||
            e.error.includes("Failed to load image from url"))
        ) {
          const urlWithoutJpg = stateUrl.replace(".jpg", "");
          console.warn(
            "404 error, attempting to fall back to url w/o .jpg :",
            urlWithoutJpg,
          );
          setStateUrl(urlWithoutJpg);
          return;
        }

        if (e.error.includes("timed out")) {
          if (timeoutRetryCount.current >= 1) {
            setPlaceholder(systemPlaceholder);
            return;
          }
          timeoutRetryCount.current += 1;
          console.warn("timed out, attempting to reload original image");
          const originalUrl = stateUrl;
          const isCurrentRecovery = captureRecovery();
          setStateUrl("reload_image");
          setTimeout(() => {
            if (!isCurrentRecovery()) return;
            setStateUrl(originalUrl);
          }, 50);
          return;
        }

        if (stateUrl?.includes("file")) {
          fixDeletedLocalUrl(stateUrl);
        } else {
          const newUrl = getNextArtworkFallbackUrl(stateUrl);

          if (newUrl) {
            console.log("falling back to:", newUrl);
            setStateUrl(newUrl);
            return;
          }
        }

        setPlaceholder(systemPlaceholder);
      }}
      onLoadStart={() => {
        if (loadStarted.current || !stateUrl) return;
        loadStarted.current = Date.now();
      }}
      onLoad={() => {
        if (hadError && !placeholder) {
          setHadError(false);
        }
        timeoutRetryCount.current = 0;
        if (wasCancelled) {
          setWasCancelled(false);
        }
      }}
    />
  );
};

const TracklistChip = ({ compact = false }: { compact?: boolean }) => (
  <LBox
    borderRadius="max"
    bg="screenBg"
    position="absolute"
    bottom={3}
    alignSelf="center"
    style={{ padding: 1 }}
  >
    <LBox
      borderRadius="max"
      style={{ backgroundColor: "rgb(4, 36, 31)" }}
      px={compact ? "s0_75" : "s1_25"}
      py="s0_25"
    >
      <LText
        variant="Caption/C2/Medium"
        style={{ color: "rgb(102, 232, 195)" }}
      >
        DJ Set
      </LText>
    </LBox>
  </LBox>
);

const LArtwork = ({
  blurhash,
  borderRadius = "zero",
  imageSize,
  imageStyle,
  playlistHorizontal,
  priority,
  size,
  transition,
  url: uri,
  userAvatar = false,
  placeholderImage,
  imageGroupKey,
  showTracklistChip = false,
  tracklistChipCompact = false,
  ...rest
}: LArtworkProps) => {
  // Read pathname once at mount time — not reactively. This prevents
  // every LArtwork from re-keying (and getting cancelled) when the
  // global pathname changes during navigation.
  const [mountPathname] = useState(
    () => useNavigationStore.getState().currentPath,
  );
  const keyOverride = useImageGroupKeyOverride();

  const serializedKey = useMemo(
    () =>
      serializeKey(
        imageGroupKey ?? keyOverride ?? pathnameToKey(mountPathname),
      ),
    [imageGroupKey, keyOverride, mountPathname],
  );

  // Register/unregister this group in the JS-side registry
  useEffect(() => {
    registerGroup(serializedKey);
    return () => unregisterGroup(serializedKey);
  }, [serializedKey]);

  const urls = Array.isArray(uri) ? uri : [uri];
  const blurhashes = Array.isArray(blurhash) ? blurhash : [blurhash];
  const urlsWithValue = urls.filter((x) => x) as string[];
  const uniqueUrls = _.uniq(urlsWithValue);
  const isCollage = uniqueUrls.length >= 4;

  const imageSizeParams =
    imageSize === "large" && isCollage ? "medium" : imageSize;

  const uniqueUrlsWithSize = uniqueUrls.map((url) => {
    return imageSizeParams ? getImageUrlWithSize(url, imageSizeParams) : url;
  });

  if (!isCollage) {
    return (
      <LBox
        {...rest}
        borderRadius={borderRadius}
        height={rest.height || size}
        overflow="hidden"
        width={rest.width || size}
      >
        <PureArtwork
          blurhash={blurhashes.at(0)}
          priority={priority}
          size={size}
          style={imageStyle}
          transition={transition}
          url={uniqueUrlsWithSize.at(0)}
          userAvatar={userAvatar}
          placeholderImage={placeholderImage}
          serializedGroupKey={serializedKey}
        />
        {showTracklistChip ? (
          <TracklistChip compact={tracklistChipCompact} />
        ) : null}
      </LBox>
    );
  } else {
    const sizeNumber = Number(size);
    const sectionSize = playlistHorizontal ? sizeNumber / 2 : sizeNumber;
    const inidividualSize = playlistHorizontal
      ? sizeNumber / 4
      : sizeNumber / 2;
    return (
      <LBox
        {...rest}
        borderRadius={borderRadius}
        flexDirection={playlistHorizontal ? "row" : "column"}
        overflow="hidden"
        width={size}
      >
        <LBox flexDirection="row" width={sectionSize}>
          <PureArtwork
            blurhash={blurhashes.at(0)}
            priority={priority}
            size={inidividualSize}
            style={imageStyle}
            transition={transition}
            url={uniqueUrlsWithSize.at(0)}
            serializedGroupKey={serializedKey}
          />
          <PureArtwork
            blurhash={blurhashes.at(1)}
            priority={priority}
            size={inidividualSize}
            style={imageStyle}
            transition={transition}
            url={uniqueUrlsWithSize.at(1)}
            serializedGroupKey={serializedKey}
          />
        </LBox>
        <LBox flexDirection="row" width={sectionSize}>
          <PureArtwork
            size={inidividualSize}
            url={uniqueUrlsWithSize.at(2)}
            style={imageStyle}
            priority={priority}
            blurhash={blurhashes.at(2)}
            transition={transition}
            serializedGroupKey={serializedKey}
          />
          <PureArtwork
            blurhash={blurhashes.at(3)}
            priority={priority}
            size={inidividualSize}
            style={imageStyle}
            transition={transition}
            url={uniqueUrlsWithSize.at(3)}
            serializedGroupKey={serializedKey}
          />
        </LBox>
        {showTracklistChip ? (
          <TracklistChip compact={tracklistChipCompact} />
        ) : null}
      </LBox>
    );
  }
};

export default memo(LArtwork);
